import i18next from 'i18next';

/**
 * The sentence a person sees when an API call fails — built in ONE place, in the active UI language.
 *
 * <p>Until 2026-10-21 every fetch in `services/conversionsApi.ts` built its own: nine copies of
 * `'Request timed out. Please try again.'`, eight of `` `HTTP ${status}` ``, plus `'Unknown error'`
 * and `` `API Error: ${status}` `` — all English, all reachable (this API is Y1 consumption, so a cold
 * start is exactly when a timeout fires), and <b>counted by neither i18n sweep</b>, which is why the
 * Spanish audit first reported seven client sites instead of twenty-six.</p>
 *
 * <p>Order of preference, most specific first:</p>
 * <ol>
 *   <li>the server's machine-readable <code>code</code>, when the client has a sentence for it
 *       (`serverErrors.codes.*`; `api/Services/ApiErrorCodes.cs` is the list, and a backend test
 *       fails if any code lacks a sentence in either language);</li>
 *   <li>the server's own <code>error</code> sentence — still English for the errors not yet given a
 *       code, but it says what happened, which a status number does not;</li>
 *   <li>a localized sentence naming the HTTP status.</li>
 * </ol>
 */
export interface ApiErrorBody {
  error?: string;
  code?: string;
  /**
   * A specific the server could not translate (a parser's reason, a list of role names). A coded sentence
   * that has a `{{detail}}` slot shows it INSIDE the localized sentence (fleet i18n standard §2), never
   * after an English preamble. Sentences without the slot ignore it.
   */
  detail?: string | null;
}

export function apiErrorMessage(status: number, body: ApiErrorBody | null | undefined): string {
  const code = body?.code;
  if (code && i18next.exists(`serverErrors.codes.${code}`)) {
    return i18next.t(`serverErrors.codes.${code}`, { detail: body?.detail ?? '' });
  }
  if (body?.error?.trim()) return body.error;
  return i18next.t('serverErrors.httpStatus', { status });
}

/** One item of the journal export's `errors[]`: a rule in the server's ManualJournalValidator. */
export interface JournalValidationErrorBody {
  message: string;
  lineIndex?: number | null;
  code?: string | null;
  /** Invariant values the coded sentence needs: amounts unformatted (`10901.92`), dates `yyyy-MM-dd`. */
  args?: Record<string, string> | null;
}

const INVARIANT_AMOUNT = /^-?\d+\.\d{2}$/;

/**
 * A validation error as a sentence in the person's language, prefixed with its line number when it has one. Amounts
 * are formatted for the SCREEN's language (`10.901,92` in Spanish), because the server sends them invariant. Without a
 * code this client knows, it is the server's English sentence, still line-numbered.
 */
export function journalValidationMessage(error: JournalValidationErrorBody): string {
  const key = `serverErrors.codes.${error.code}`;
  const text =
    error.code && i18next.exists(key)
      ? i18next.t(key, formatArgs(error.args))
      : error.message;
  return error.lineIndex == null
    ? text
    : i18next.t('serverErrors.lineNumbered', { line: error.lineIndex + 1, message: text });
}

function formatArgs(args: Record<string, string> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(args ?? {})) {
    out[name] = INVARIANT_AMOUNT.test(value)
      ? Number(value).toLocaleString(i18next.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : value;
  }
  return out;
}

/** The request was aborted after the client's timeout. */
export function timeoutMessage(): string {
  return i18next.t('serverErrors.timeout');
}

/** An error response's JSON envelope, or null when the body is not JSON (a proxy's HTML page, say). */
export async function readErrorBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    return (await response.json()) as ApiErrorBody;
  } catch {
    return null;
  }
}
